<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'

import { HeadingType } from '@xcel-next/core/enums'

import ImageSourceSet from '../shared/image/SourceSet.vue'
import { ParagraphTypeInterline } from '../paragraph-types'
import EmbedContent from '../paragraph-types/ParagraphTypeIframe/EmbedContent.vue'
import { useAdaPagestrip } from '../../composables/use-ada-pagestrip'

import type { ComposedMetaData } from '@xcel-next/composer-schema'
import type { Brand } from '@xcel-next/core/enums'

type NativeHeader = NonNullable<ComposedMetaData['nativeHeader']>

const props = withDefaults(defineProps<{
  nativeHeader?: NativeHeader
  brand?: Brand
}>(), {
  nativeHeader: undefined,
  brand: undefined
})

const { pagestripHeaderId, target, rel, initPagestrip } = useAdaPagestrip()

const pagestripHeightRatio = ref('27.13%')
let intervalId: ReturnType<typeof setInterval> | undefined

const headingTag = computed(() => props.nativeHeader?.headline?.url?.length ? 'a' : 'div')
const isHeadlineLink = computed(() => (props.nativeHeader?.headline?.url?.length ?? 0) > 0)
const showPagestripNativeHeader = computed(() => (props.nativeHeader?.pageStrip?.length ?? 0) > 0)

const headlineLinkAttributes = computed(() => {
  if (!isHeadlineLink.value) return {}
  return {
    href: props.nativeHeader!.headline!.url,
    target: target.value,
    rel: rel.value
  }
})

function calculateHeightRatio (pagestripContent: Element) {
  const boundingRect = pagestripContent.getBoundingClientRect()
  const currentPagestripHeightRatio = `${((boundingRect.height / boundingRect.width) * 100).toFixed(2)}%`
  if (currentPagestripHeightRatio !== pagestripHeightRatio.value) {
    pagestripHeightRatio.value = currentPagestripHeightRatio
  }
}

onMounted(() => {
  // for my future self or someone else who is urgently looking for a pageStrip ID for the native header in the article.
  // Here it is, with location where to enter it
  // Article > Ad-Settings > Native Header > Header Typ: pagestrip > Id: IifbXdmm
  if (showPagestripNativeHeader.value) {
    pagestripHeaderId.value = props.nativeHeader?.pageStrip ?? ''
    initPagestrip()
    // Set an interval to check the current pagestrip height ratio when pagestrip content is loaded
    intervalId = setInterval(() => {
      const pagestripContent = document.querySelector('#pagestrip-header > div > div > div')
      if (pagestripContent) {
        calculateHeightRatio(pagestripContent)
        clearInterval(intervalId) // Clear the interval once the div is found
      }
    }, 1000)
  }
})

onUnmounted(() => {
  clearInterval(intervalId)
})
</script>

<template>
  <div class="bx-ada-native-header bx-typo--ada-native-header"
       data-tc="ada-native-header">
    <component :is="headingTag"
               v-if="nativeHeader?.headline?.text"
               :class="{ 'bx-typo--link': headingTag === 'a' }"
               v-bind="headlineLinkAttributes">
      <ParagraphTypeInterline :interline="{ text: nativeHeader.headline.text, heading: HeadingType.h2 }" />
    </component>
    <div v-if="nativeHeader?.image"
         class="bx-content">
      <ImageSourceSet :image="nativeHeader.image"
                      :brand="brand"
                      :target="target"
                      :rel="rel" />
    </div>
    <div v-if="nativeHeader?.iFrame"
         class="bx-content bx-content--iframe bx-ada-native-header__iframe">
      <div>
        <EmbedContent :url="nativeHeader.iFrame" />
      </div>
    </div>
    <div v-if="showPagestripNativeHeader"
         class="bx-content bx-ada-native-header__pagestrip">
      <div id="pagestrip-header"
           :style="`padding-bottom: ${pagestripHeightRatio}`" />
    </div>
  </div>
</template>
